import * as React from 'react'
import { useField, useFormikContext } from 'formik'

import { currencyString } from '@sales-center/common'
import { LoadingSpinner } from '@sales-center/components'
import { useGetActiveServicesOnMount } from '@user/store/opportunity'
import type { ICancelReason } from '@user/store/scheduling'

import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Divider from '@mui/material/Divider'

const HELPER_TEXT =
  'You can choose to cancel active service order/setups on the PestPac location. This cannot be undone. Your reporting may be affected given this was completed via the Closed/Won queue but will be reflected as closed/lost.'

type FieldShape = {
  selectedServiceIds: string[]
  cancelConfirmation: string
  notServicedReason: string
}

type Props = {
  opportunityId: number
  showHeader?: boolean
  showCancelReason?: boolean
  showDivider?: boolean
  showEmptyAlert?: boolean
  cancelReasons?: ICancelReason[]
}

export function CancelServicesSection(props: Props) {
  const {
    opportunityId,
    showHeader = false,
    showCancelReason = false,
    showDivider = false,
    showEmptyAlert = false,
    cancelReasons = [],
  } = props
  const { loading, activeServices: services } =
    useGetActiveServicesOnMount(opportunityId)

  const { setFieldValue, submitCount } = useFormikContext<FieldShape>()
  const [selectedField] = useField<string[]>('selectedServiceIds')
  const [confirmField, confirmMeta] = useField<string>('cancelConfirmation')
  const [reasonField, reasonMeta] = useField<string>('notServicedReason')

  const showConfirmError = submitCount > 0 && !!confirmMeta.error
  const showReasonError = submitCount > 0 && !!reasonMeta.error

  const selected = selectedField.value
  const hasSelection = selected.length > 0

  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id]
    setFieldValue('selectedServiceIds', next)
  }

  if (loading) {
    return (
      <Box minHeight="48px">
        <LoadingSpinner />
      </Box>
    )
  }

  if (services.length === 0) {
    if (!showEmptyAlert) return null

    return (
      <Alert severity="info" data-test-id="cancel-services-empty-alert">
        There are no active service orders/setups to cancel.
      </Alert>
    )
  }

  return (
    <>
      {showDivider && <Divider sx={{ my: 2 }} />}
      {services.length > 0 && (
        <Stack mt={showHeader ? 2 : 0} spacing={1.5}>
          {showHeader && (
            <Typography variant="subtitle3">
              Cancel Services (optional)
            </Typography>
          )}
          <Typography variant="body2">{HELPER_TEXT}</Typography>

          <Paper variant="outlined" sx={{ borderRadius: 1, px: 1.5, py: 0.5 }}>
            {services.map((service) => {
              const serviceId = String(service.serviceId)
              return (
                <Box
                  key={serviceId}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  data-test-id={`cancel-service-row-${serviceId}`}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selected.includes(serviceId)}
                        onChange={() => toggle(serviceId)}
                        data-test-id={`cancel-service-checkbox-${serviceId}`}
                      />
                    }
                    label={service.description || service.name}
                  />
                  <Typography variant="subtitle3">
                    {'$' + currencyString(service.total)}
                  </Typography>
                </Box>
              )
            })}
          </Paper>

          {showCancelReason && (
            <Stack>
              <Typography variant="subtitle3" mb={1}>
                Select a cancellation reason*
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Non-billable estimates will be canceled automatically.
              </Alert>
              <TextField
                {...reasonField}
                select
                fullWidth
                label="Not Serviced Reason"
                error={showReasonError}
                helperText={showReasonError ? reasonMeta.error : undefined}
                data-test-id="cancel-services-reason-select"
              >
                {cancelReasons.map((r) => (
                  <MenuItem key={r.id} value={r.name}>
                    {`${r.name} - ${r.description}`}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}

          {hasSelection && (
            <Stack spacing={1}>
              <Typography variant="body2">
                To confirm cancellation, you must type &apos;CANCEL&apos; below.{' '}
                <Typography variant="subtitle3" component="span">
                  This action cannot be undone.
                </Typography>
              </Typography>
              <TextField
                {...confirmField}
                fullWidth
                label="Cancel confirmation"
                placeholder="Type 'CANCEL'"
                error={showConfirmError}
                helperText={showConfirmError ? confirmMeta.error : undefined}
                data-test-id="cancel-services-confirm-input"
              />
            </Stack>
          )}
        </Stack>
      )}
    </>
  )
}
